import { useState } from 'react';
import { RetourPage } from '../../commun/RetourPage';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { REPORT_CATEGORIES, SAFETY_MESSAGE_ELECTRIC, type Domain } from '@eec/contrats';
import { requete, requetePublique } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { MessagesPublics } from './MessagesPublics';
import { ReclamationPubliqueGenerale } from './ReclamationPubliqueGenerale';
import { ETATS_RECLAMATION_RESEAU } from '@eec/contrats';
export function PagePortailPublic() {
  const [domaine, setDomaine] = useState<Domain>('WATER');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [code, setCode] = useState('');
  const [recu, setRecu] = useState<{
    reference: string;
    code_suivi: string;
    message: string;
  } | null>(null);
  const [suivi, setSuivi] = useState<{
    reference: string;
    statut: string;
    recu_le: string;
    peut_donner_avis: boolean;
    avis_recu: boolean;
    reclamation: {
      statut: keyof typeof ETATS_RECLAMATION_RESEAU;
      reponse: string | null;
      repondu_le: string | null;
    } | null;
  } | null>(null);
  const ref = useQuery({
    queryKey: ['portail-public', 'referentiel'],
    queryFn: () =>
      requete<{
        organisations: {
          id: string;
          nom: string;
          domaine: string;
          province_id: string | null;
          fictive: boolean;
        }[];
        provinces: { id: string; nom: string }[];
      }>('/portail-public/referentiel'),
  });
  return (
    <main className="eec-metier" style={{ maxWidth: 850, margin: 'auto', padding: 24 }}>
      <h1>EauÉnergie Connect — Signalement citoyen</h1>
      <RetourPage vers="/connexion" libelle="Retour à la connexion" />
      <Link to="/connexion">Espace professionnel</Link>
      <MessagesPublics />
      <p>
        Signalez une interruption d’eau ou d’électricité. Le signalement sera vérifié par un agent ;
        il ne garantit pas une intervention immédiate.
      </p>
      <p role="note">{SAFETY_MESSAGE_ELECTRIC}</p>
      {(erreur || ref.error) && <p role="alert">{erreur || messageErreur(ref.error)}</p>}
      {ref.isPending && <p>Chargement des services…</p>}
      {ref.data?.organisations.length === 0 && (
        <p>Aucun exploitant n’a ouvert la réception des signalements sur ce portail.</p>
      )}
      {!recu && Boolean(ref.data?.organisations.length) && (
        <form
          style={{ display: 'grid', gap: 12 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            setErreur('');
            setEnCours(true);
            try {
              const r = await requetePublique<{
                reference: string;
                code_suivi: string;
                message: string;
              }>('/portail-public/signalements', {
                organisation_id: d.get('organisation_id'),
                province_id: d.get('province_id'),
                domaine,
                categorie: d.get('categorie'),
                description: d.get('description'),
                localisation: d.get('localisation'),
                securite_lue: d.get('securite_lue') === 'on',
                site_web: d.get('site_web'),
              });
              setRecu(r);
              setCode(r.code_suivi);
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setEnCours(false);
            }
          }}
        >
          <h2>Nouveau signalement</h2>
          <label>
            Service{' '}
            <select value={domaine} onChange={(e) => setDomaine(e.target.value as Domain)}>
              <option value="WATER">Eau</option>
              <option value="ELECTRICITY">Électricité</option>
            </select>
          </label>
          <label>
            Exploitant{' '}
            <select name="organisation_id" required key={domaine}>
              <option value="">Choisir</option>
              {ref.data?.organisations
                .filter(
                  (o) =>
                    o.domaine === (domaine === 'WATER' ? 'eau' : 'electricite') ||
                    o.domaine === 'mixte',
                )
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nom}
                    {o.fictive ? ' (démonstration)' : ''}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Province{' '}
            <select name="province_id" required>
              <option value="">Choisir</option>
              {ref.data?.provinces.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nom}
                </option>
              ))}
            </select>
          </label>
          <label>
            Catégorie{' '}
            <select name="categorie" key={`categorie-${domaine}`}>
              {REPORT_CATEGORIES[domaine].map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Localisation{' '}
            <input
              name="localisation"
              required
              minLength={3}
              maxLength={250}
              placeholder="Ville, quartier et repère"
            />
          </label>
          <label>
            Description <textarea name="description" required minLength={5} maxLength={2000} />
          </label>
          <p>
            Évitez d’inscrire des informations personnelles inutiles dans la description. Aucun
            compte, nom ou numéro de téléphone n’est nécessaire.
          </p>
          <label>
            <input type="checkbox" name="securite_lue" />
            J’ai lu les consignes de sécurité
          </label>
          <div hidden aria-hidden="true">
            <label>
              Site web <input name="site_web" tabIndex={-1} autoComplete="off" />
            </label>
          </div>
          <button disabled={enCours}>Envoyer le signalement</button>
        </form>
      )}
      {recu && (
        <section role="status">
          <h2>Signalement enregistré</h2>
          <p>{recu.message}</p>
          <p>Référence : {recu.reference}</p>
          <label>
            Votre code secret de suivi <textarea readOnly value={recu.code_suivi} />
          </label>
          <p>
            Conservez ce code : il est nécessaire au suivi et ne sera pas récupérable en cas de
            perte. Ne le partagez pas publiquement.
          </p>
          <button onClick={() => setRecu(null)}>Autre signalement</button>
        </section>
      )}
      <form
        style={{ display: 'grid', gap: 12, marginTop: 32 }}
        onSubmit={async (e) => {
          e.preventDefault();
          setErreur('');
          setSuivi(null);
          setEnCours(true);
          try {
            setSuivi(await requetePublique('/portail-public/suivi', { code: code.trim() }));
          } catch (err) {
            setErreur(messageErreur(err));
          } finally {
            setEnCours(false);
          }
        }}
      >
        <h2>Suivre mon signalement</h2>
        <label>
          Code secret de suivi{' '}
          <input
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setSuivi(null);
            }}
            required
            autoComplete="off"
            maxLength={80}
          />
        </label>
        <button disabled={enCours}>Consulter le suivi</button>
      </form>
      {suivi && (
        <section role="status">
          <h3>{suivi.reference}</h3>
          <p>{suivi.statut}</p>
          <p>Reçu le {new Date(suivi.recu_le).toLocaleString('fr-FR')}</p>
          <p>Estimation du rétablissement indisponible.</p>
          {suivi.reclamation ? (
            <section aria-label="Suivi de ma réclamation">
              <h3>Votre réclamation</h3>
              <p>{ETATS_RECLAMATION_RESEAU[suivi.reclamation.statut]}</p>
              {suivi.reclamation.reponse && <p>{suivi.reclamation.reponse}</p>}
            </section>
          ) : (
            <form
              style={{ display: 'grid', gap: 12 }}
              onSubmit={async (e) => {
                e.preventDefault();
                const d = new FormData(e.currentTarget);
                setEnCours(true);
                setErreur('');
                try {
                  await requetePublique('/portail-public/reclamations', {
                    code: code.trim(),
                    contenu: d.get('contenu'),
                  });
                  setSuivi({
                    ...suivi,
                    reclamation: { statut: 'RECEIVED', reponse: null, repondu_le: null },
                  });
                } catch (err) {
                  setErreur(messageErreur(err));
                } finally {
                  setEnCours(false);
                }
              }}
            >
              <h3>Déposer une réclamation sur ce signalement</h3>
              <p>
                Décrivez le problème de traitement sans inscrire de mot de passe, de pièce
                d'identité ou de données sensibles. Une seule réclamation est ouverte par
                signalement.
              </p>
              <label>
                Objet de votre réclamation
                <textarea name="contenu" required minLength={10} maxLength={2000} />
              </label>
              <button disabled={enCours}>Envoyer ma réclamation</button>
            </form>
          )}
          {suivi.avis_recu && <p>Votre avis a été enregistré. Merci.</p>}
          {suivi.peut_donner_avis && (
            <form
              style={{ display: 'grid', gap: 12 }}
              onSubmit={async (e) => {
                e.preventDefault();
                const d = new FormData(e.currentTarget);
                setErreur('');
                setEnCours(true);
                try {
                  await requetePublique('/portail-public/retours', {
                    code: code.trim(),
                    note: Number(d.get('note')),
                    commentaire: d.get('commentaire'),
                  });
                  setSuivi({ ...suivi, avis_recu: true, peut_donner_avis: false });
                } catch (err) {
                  setErreur(messageErreur(err));
                } finally {
                  setEnCours(false);
                }
              }}
            >
              <h3>Votre avis après rétablissement</h3>
              <label>
                Satisfaction{' '}
                <select name="note" required defaultValue="">
                  <option value="">Choisir une note</option>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n}/5
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Commentaire facultatif <textarea name="commentaire" maxLength={1000} />
              </label>
              <p>
                Évitez les informations personnelles. Un seul avis est accepté par signalement ; cet
                avis ne crée pas une nouvelle demande d’intervention.
              </p>
              <button disabled={enCours}>Envoyer mon avis</button>
            </form>
          )}
        </section>
      )}
      <ReclamationPubliqueGenerale organisations={ref.data?.organisations ?? []} />
    </main>
  );
}
